import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('adminToken');
  console.log('API Request:', config.method?.toUpperCase(), config.url);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
    console.log('Token attached:', token.substring(0, 20) + '...');
  } else {
    console.warn('No admin token found in localStorage');
  }
  return config;
});

// Add response interceptor for better error handling
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.status, error.response?.data);
    if (error.response?.status === 401) {
      console.error('Unauthorized - token may have expired');
      localStorage.removeItem('adminToken');
      window.location.href = '/admin/login';
    }
    return Promise.reject(error);
  }
);

// Public API endpoints
export const maidService = {
  getAll: (params) => api.get('/maids', { params }),
  getById: (id) => api.get(`/maids/${id}`),
};

export const serviceService = {
  getAll: () => api.get('/services'),
  getById: (id) => api.get(`/services/${id}`),
};

export const newsService = {
  getAll: (limit = 10) => api.get('/news', { params: { limit } }),
  getById: (id) => api.get(`/news/${id}`),
};

export const faqService = {
  getAll: (category) => api.get('/faqs', { params: { category } }),
};

export const contactService = {
  submit: (data) => api.post('/contact', data),
};

export const contentService = {
  get: () => api.get('/content'),
};

// Admin API endpoints
export const adminService = {
  login: (credentials) => api.post('/admin/login', credentials),
  verify: () => api.get('/admin/verify'),
  
  // Maids management
  maids: {
    create: (data) => api.post('/admin/maids', data),
    update: (id, data) => api.put(`/admin/maids/${id}`, data),
    delete: (id) => api.delete(`/admin/maids/${id}`),
  },
  
  // Services management
  services: {
    update: (id, data) => api.put(`/admin/services/${id}`, data),
  },
  
  // News management
  news: {
    create: (data) => api.post('/admin/news', data),
    update: (id, data) => api.put(`/admin/news/${id}`, data),
    delete: (id) => api.delete(`/admin/news/${id}`),
  },
  
  // Contacts management
  contacts: {
    getAll: (status) => api.get('/admin/contacts', { params: { status } }),
    updateStatus: (id, status) => api.put(`/admin/contacts/${id}`, null, { params: { status } }),
  },
  
  // FAQs management
  faqs: {
    create: (data) => api.post('/admin/faqs', data),
    update: (id, data) => api.put(`/admin/faqs/${id}`, data),
    delete: (id) => api.delete(`/admin/faqs/${id}`),
  },
  
  // Site content management
  content: {
    update: (data) => api.put('/admin/content', data),
  },
  
  // File upload management
  upload: {
    uploadFile: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/admin/upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    uploadImage: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/admin/upload-image', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    },
    deleteFile: (filename) => api.delete(`/admin/upload/${filename}`),
  },
};

export default api;
